import moment from 'moment';
import { formatAddress } from '../utils/check-value';
import { URLS } from '../utils/urls';
import api from './client';

export const studentsService = {
    changePassword: async (id, credentials) => {
        const response = await api.put(`students/${id}/password`, credentials);
        return response.data;
    },
    addStudents: async (credentials) => {
        const response = await api.post('students', credentials);
        return response.data;
    },
    getAllStudents: async () => {
        const response = await api.get('students');
        return {
            ...response.data,
            data: Array.isArray(response?.data?.data) ? response?.data?.data?.map((student, index) => ({
                ...student,
                country_of_birth: student?.contact_details?.country_of_birth,
                place_of_birth: student?.contact_details?.place_of_birth,
                postal_code: student?.contact_details?.postal_code,
                birth_date: student?.contact_details?.birth_date,
                bg_number: student?.authority?.bg_number,
                phone: student?.contact_details?.phone,
                city: student?.contact_details?.city,
                studentDetails: student,
                address: formatAddress({
                    country: student?.contact_details?.country_of_birth,
                    street: student?.contact_details?.street_name,
                    pincode: student?.contact_details?.postal_code,
                    city: student?.contact_details?.city,
                }),
                no: index + 1
            })) : [],
        };
    },
    getActiveStudentAbsences: async () => {
        const response = await api.get(`attendance/active-student-absences`);
        return response.data;
    },
    getStudentById: async (id) => {
        const response = await api.get(`students/${id}`);
        return { ...response, data: { ...response.data, lecturer: response.data?.lecturer?.lecturer_id } }
    },
    updateStudents: async (id, credentials) => {
        const response = await api.put(`students/${id}`, credentials);
        return response;
    },
    deleteStudents: async (id) => {
        const response = await api.delete(`students/${id}`);
        return response.data;
    },

    // Student Exams
    addStudentExams: async (studentId, credentials) => {
        const response = await api.post(`students/${studentId}/exams`, credentials);
        return response.data;
    },
    getStudentExams: async (studentId) => {
        const response = await api.get(`students/${studentId}/exams`);
        return response.data;
    },
    deleteStudentExams: async (examId) => {
        const response = await api.delete(`exams/${examId}`);
        return response.data;
    },
    markExamStatus: async (examId, status) => {
        const response = await api.put(`exams/${examId}/result/${status}`);
        return response.data;
    },
    sendExamDocument: async (examId, credentials) => {
        const response = await api.post(`exams/${examId}/certificate`, credentials, { headers: { "Content-Type": "multipart/form-data" } });
        return response.data;
    },
    sendExamCertificate: async (examId, credentials) => {
        const response = await api.put(`exams/${examId}/certificate`, credentials);
        return response.data;
    },
    sendExamCompletionEmail: async (examId) => {
        const response = await api.post(`exams/${examId}/send-completion-email`, { completion_date: moment().format('YYYY-MM-DD') });
        return response.data;
    },
    markExamAsBilled: async (examId, credentials) => {
        const response = await api.put(`exams/${examId}/done/${credentials?.done_on}`);
        return response.data;
    },
    markExamRemarks: async (examId, credentials) => {
        const response = await api.put(`exams/${examId}/remark`, credentials);
        return response.data;
    },

    // Student Contact
    getStudentContact: async (studentId) => {
        const response = await api.get(`students/${studentId}/contact`);
        return response.data;
    },
    updateStudentContact: async (studentId, credentials) => {
        const response = await api.put(`students/${studentId}/contact`, credentials);
        return response.data;
    },

    // Student Sick Leaves
    getStudentSickLeaves: async (studentId) => {
        const response = await api.get(`students/${studentId}/sick-leaves`);
        return response.data;
    },
    addStudentSickLeaves: async (studentId, credentials) => {
        const response = await api.post(`students/${studentId}/sick-leaves`, credentials);
        return response.data;
    },
    updateStudentSickLeaves: async (id, credentials) => {
        const response = await api.put(`sick-leaves/${id}`, credentials);
        return response.data;
    },
    deleteStudentSickLeaves: async (id) => {
        const response = await api.delete(`sick-leaves/${id}`);
        return response.data;
    },

    // Student Invoices
    addStudentInvoices: async (studentId, credentials) => {
        const response = await api.post(`students/${studentId}/invoices`, credentials);
        return response.data;
    },
    getStudentInvoices: async (studentId) => {
        const response = await api.get(`students/${studentId}/invoices`);
        return response.data;
    },
    cancelInvoices: async (studentId) => {
        const response = await api.post(`invoices/${studentId}/cancel`);
        return response.data;
    },
    deleteStudentInvoices: async (invoiceId) => {
        const response = await api.delete(`invoices/${invoiceId}`);
        return response.data;
    },
    updateStudentInvoices: async (id, credentials) => {
        const response = await api.put(`invoices/${id}`, credentials);
        return response.data;
    },
    addStudentInvoicesMahnung: async (invoiceId) => {
        const response = await api.get(`invoices/${invoiceId}/mahnung`);
        return response.data;
    },
    addStudentInvoicesReminder: async (invoiceId) => {
        const response = await api.post(`invoices/${invoiceId}/reminder`);
        return response.data;
    },
    studentInvoicesAutoDispatch: async (invoiceId, credentials) => {
        const response = await api.put(`invoices/${invoiceId}/auto-dispatch`, credentials);
        return response.data;
    },

    // Student Result Sheet
    addStudentResultSheet: async (studentId, credentials) => {
        const response = await api.post(`students/${studentId}/result-sheet`, credentials);
        return response.data;
    },
    getStudentResultSheet: async (studentId) => {
        const response = await api.get(`students/${studentId}/result-sheet`);
        return response.data;
    },
    deleteStudentResultSheet: async (invoiceId) => {
        const response = await api.delete(`result-sheet/${invoiceId}`);
        return response.data;
    },
    updateStudentResultSheet: async (studentId, credentials) => {
        const response = await api.put(`students/${studentId}/result-sheet`, credentials);
        return response.data;
    },
    genrateResultSheet: async (studentId, credentials) => {
        const response = await api.post(`students/${studentId}/result-sheet/generate-pdf`, credentials);
        return response.data;
    },
    getStudentResultSheetPdfs: async (studentId) => {
        const response = await api.get(`students/${studentId}/result-sheet/pdfs`);
        return response.data;
    },
    deleteStudentResultSheetPdf: async (id) => {
        const response = await api.delete(`students/result-sheet/pdfs/${id}`);
        return response.data;
    },
    emailStudentResultSheetPdf: async (id, credentials) => {
        const response = await api.post(`students/result-sheet/pdfs/${id}/send`, credentials);
        return response.data;
    },

    // Create a discharge report and termination report
    addStudentDischargeReport: async (studentId, credentials) => {
        const response = await api.post(`students/${studentId}/reports/discharge`, credentials);
        return response.data;
    },
    addStudentTerminationReport: async (studentId, credentials) => {
        const response = await api.post(`students/${studentId}/reports/termination`, credentials);
        return response.data;
    },
    getStudentDischargeTerminationReport: async (studentId) => {
        const response = await api.get(`students/${studentId}/reports`);
        return response.data;
    },
    deleteDischargeTerminationReport: async (id) => {
        const response = await api.delete(`reports/${id}`);
        return response.data;
    },

    // Certificate of absence
    addStudentCertificateAbsence: async (studentId, credentials) => {
        const response = await api.get(`students/${studentId}/sick-leaves/monthly/${credentials?.month}/${credentials?.year}`);
        return response.data;
    },
    getStudentCertificateAbsence: async (studentId) => {
        const response = await api.get(`certificates-of-absence/student/${studentId}`);
        return response.data;
    },
    deleteCertificateAbsence: async (id) => {
        const response = await api.delete(`certificates-of-absence/${id}`);
        return response.data;
    },
    certificateOfAbsenceSendEmail: async (id, credentials) => {
        const response = await api.post(`certificates-of-absence/send-email`, credentials);
        return response.data;
    },

    // Attendance
    createAttendancePdf: async (credentials) => {
        const response = await api.post(`attendance-lists`, credentials);
        return response.data;
    },
    getStudentAttendancePdf: async (studentId) => {
        const response = await api.get(`attendance-lists?student_id=${studentId}`);
        return response.data;
    },


    // Mark attendance
    markAttendance: async (studentId, credentials) => {
        const response = await api.post(`attendance/students/${studentId}`, credentials);
        return response.data;
    },
    getMissedAttendance: async (studentId) => {
        const response = await api.get(`attendance/full-day-absences/${studentId}`);
        return response.data;
    },

    // Files
    addStudentFiles: async (studentId, credentials) => {
        const response = await api.post(`students/${studentId}/files`, credentials, { headers: { "Content-Type": "multipart/form-data" } });
        return response.data;
    },
    getStudentFiles: async (studentId) => {
        const response = await api.get(`students/${studentId}/files`);
        return response.data;
    },
    deleteStudentFiles: async (id) => {
        const response = await api.delete(`files/${id}`);
        return response.data;
    },

    // Documents
    getStudentDocuments: async (studentId) => {
        const response = await api.get(`students/${studentId}/documents`);
        return response.data;
    },


    // Reasons for absence
    addStudentReasonsAbsence: async (studentId, credentials) => {
        const response = await api.post(`students/${studentId}/reasons-of-absence`, credentials);
        return response.data;
    },
    getStudentReasonsAbsence: async (studentId) => {
        const response = await api.get(`students/${studentId}/reasons-of-absence`);
        return response.data;
    },


    getLecturerStudents: async () => {
        const response = await api.get('students');
        return {
            ...response.data,
            data: Array.isArray(response?.data?.data) ? response.data.data?.map((row, index) => ({
                ...row,
                students: Array.isArray(row?.students) ? row?.students?.map((student, index) => ({
                    ...student,
                    country_of_birth: student?.contact_details?.country_of_birth,
                    place_of_birth: student?.contact_details?.place_of_birth,
                    postal_code: student?.contact_details?.postal_code,
                    birth_date: student?.contact_details?.birth_date,
                    bg_number: student?.authority?.bg_number,
                    phone: student?.phone,
                    city: student?.contact_details?.city,
                    studentDetails: student,
                    address: formatAddress({
                        country: student?.contact_details?.country_of_birth,
                        street: student?.contact_details?.street_name,
                        pincode: student?.contact_details?.postal_code,
                        city: student?.contact_details?.city,
                    }),
                    no: index + 1
                })) : []
            })) : []
        };
    },
    studentRemarks: async (credentials) => {
        const response = await api.post(`remarks`, credentials);
        return response.data;
    },

    // Assessment
    endAssessments: async (credentials) => {
        const response = await api.post(`end-assessments`, credentials);
        return response.data;
    },
    interimAssessments: async (credentials) => {
        const response = await api.post(`interim-assessments`, credentials);
        return response.data;
    },
    getAssessments: async (studentId) => {
        try {
            const response = await api.get(`interim-assessments/student/${studentId}`);
            const responseEnd = await api.get(`students/${studentId}/end-assessment-pdfs`);
            var interims = Array.isArray(response.data?.data) ? response.data?.data?.map((row, index) => ({ ...row, type: "interim" })) : []
            var ends = Array.isArray(responseEnd.data?.data) ? responseEnd.data?.data?.map((row, index) => ({ ...row, type: "final" })) : []
            var data = [...interims, ...ends]?.sort((a, b) => new Date(b?.created_at) - new Date(a?.created_at))
            return data;
        } catch (error) {
            return [];
        }
    },
    deleteAssessment: async (id) => {
        const response = await api.delete(`end-assessments/${id}`);
        return response.data;
    },
    deleteInterimAssessmentPdf: async (id) => {
        const response = await api.delete(`interim-assessments/${id}`);
        return response.data;
    },
    // Learning Progress
    getQuizzes: async (studentId) => {
        const response = await api.get(`quizzes/students/${studentId}/results`);
        return response.data;
    },
    getSpeakingExams: async (studentId) => {
        const response = await api.get(`students/${studentId}/speaking-exams`);
        return response.data;
    },
    getVideoLogs: async (studentId) => {
        const response = await api.get(`video-logs?studentId=${studentId}`);
        return response.data;
    },
    fridayExamAttempts: async (studentId) => {
        const response = await api.get(`friday-exam/attempts?student_id=${studentId}`);
        return response.data;
    },

    // Student Logs
    getStudentLogs: async (year) => {
        const response = await api.get(`active-students-report/logs?year=${year}`);
        return response.data;
    },
    generateStudentLogs: async (month) => {
        const response = await api.get(`active-students-report?month=${month}`);
        return response.data;
    },
    getStudentLogsById: async (id) => {
        const response = await api.get(`active-students-report/logs/${id}`);
        return response.data;
    }
}